import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { useAllJobs } from "../../../../hooks/useJobs";
import { type MergeEntryPoint, useMergeCandidates, useMergeNurses, useNurseMergePreview } from "../../../../hooks/useNurseMerge";
import type { MergeCandidate, NurseMergeResult } from "../../../../utils/nurseMergeModels";
import { buildMergeDecisions, EMPTY_MERGE_CHOICES, type MergeChoices, needsDecisionStep, pendingDecisionCount } from "./mergeLogic";

export type MergeStepId = "find" | "compare" | "decide" | "confirm";

/** Names for the ids the merge endpoints send, resolved from data the app already holds. */
export type MergeLookups = {
	departmentName: (departmentId: string) => string;
	jobName: (jobId: string) => string;
};

/** Long enough that typing a name fires one search, short enough that the list keeps up with a pause. */
const SEARCH_DEBOUNCE_MS = 300;

/** The record picked as the other half of the pair — found by search, or already known from a suggestion. */
type ChosenCandidate = Pick<MergeCandidate, "nurseId">;

type Args = {
	currentNurse: { id: number; firstName: string; lastName: string };
	/** Set when both records are already known (a suggested pair): the flow opens on Compare with this one chosen. */
	pairedNurseId?: number;
	entryPoint: MergeEntryPoint;
	onMerged: (result: NurseMergeResult) => void;
};

/**
 * All of the merge dialog's state: the search, the picked record, which record is kept, the person's
 * choices, and the preview/merge calls. The record kept defaults to `currentNurse` — the profile being
 * viewed, or the first record of a suggested pair.
 */
export const useMergeDuplicateFlow = ({ currentNurse, pairedNurseId, entryPoint, onMerged }: Args) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const { data: allJobs } = useAllJobs();

	const [stepId, setStepId] = useState<MergeStepId>(pairedNurseId === undefined ? "find" : "compare");
	const [query, setQuery] = useState("");
	const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
	const [candidate, setCandidate] = useState<ChosenCandidate | null>(pairedNurseId === undefined ? null : { nurseId: pairedNurseId });
	const [keepNurseId, setKeepNurseId] = useState(currentNurse.id);
	const [choices, setChoices] = useState<MergeChoices>(EMPTY_MERGE_CHOICES);

	useEffect(() => {
		track("nurse_merge_dialog_opened", { department_id: departmentId, nurse_id: currentNurse.id, candidate_nurse_id: pairedNurseId, entry_point: entryPoint });
	}, [track, departmentId, currentNurse.id, pairedNurseId, entryPoint]);

	const candidates = useMergeCandidates({ departmentId, query: debouncedQuery, excludeNurseId: currentNurse.id });
	const otherNurseId = candidate === null ? null : keepNurseId === currentNurse.id ? candidate.nurseId : currentNurse.id;
	const previewQuery = useNurseMergePreview({ primaryNurseId: candidate === null ? null : keepNurseId, duplicateNurseId: otherNurseId });
	const merge = useMergeNurses({ departmentId, entryPoint, onMerged });

	// The preview on screen may still be the previous choice's while the new one loads; only a preview of
	// exactly this pair, this way round, can be confirmed.
	const preview = previewQuery.data;
	const isPreviewCurrent = !!preview && preview.primary.nurseId === keepNurseId && preview.duplicate.nurseId === otherNurseId;
	const isSamePair = !!preview && otherNurseId !== null && new Set([preview.primary.nurseId, preview.duplicate.nurseId, keepNurseId, otherNurseId]).size === 2;

	const lookups = useMemo<MergeLookups>(() => {
		const departments = new Map(userInfo.departments.map((department) => [department.department_id, department.display_name || department.name]));
		// DEFAULT: until the job catalog loads (and for a department with no jobs) there is nothing to resolve; the fallback below names the job.
		const jobs = new Map([...(allJobs ?? []), ...(chosenDepartment.jobs ?? [])].map((job) => [job.id, job.displayName]));
		return {
			// DEFAULT: a network facility the support user's department list doesn't carry still has to be named somehow — its id is what support looks up.
			departmentName: (id) => departments.get(id) ?? id,
			// DEFAULT: a job id missing from the catalog (deleted since) reads as an unknown job rather than a raw UUID.
			jobName: (id) => jobs.get(id) ?? t("nurse.merge.compare.unknown.job"),
		};
	}, [userInfo.departments, allJobs, chosenDepartment.jobs, t]);

	const selectCandidate = useCallback(
		(next: MergeCandidate) => {
			track("nurse_merge_candidate_selected", {
				department_id: departmentId,
				nurse_id: currentNurse.id,
				candidate_nurse_id: next.nurseId,
				entry_point: entryPoint,
			});
			setCandidate(next);
			setKeepNurseId(currentNurse.id);
			setChoices(EMPTY_MERGE_CHOICES);
			setStepId("compare");
		},
		[track, departmentId, currentNurse.id, entryPoint],
	);

	const changeKeep = useCallback(
		(nurseId: number) => {
			if (nurseId === keepNurseId) return;
			track("nurse_merge_keep_changed", { department_id: departmentId, keep_nurse_id: nurseId, entry_point: entryPoint });
			setKeepNurseId(nurseId);
		},
		[keepNurseId, track, departmentId, entryPoint],
	);

	const orderedNurseIds: [number, number] | null = candidate === null ? null : [currentNurse.id, candidate.nurseId];

	const hasDecisionStep = isPreviewCurrent && preview ? needsDecisionStep(preview) : false;
	const continueFromCompare = useCallback(() => setStepId(hasDecisionStep ? "decide" : "confirm"), [hasDecisionStep]);

	// A refreshed preview (after "the records changed") can stop needing decisions; the step then no longer
	// exists, and the dialog would otherwise fall back to its first step with no explanation.
	useEffect(() => {
		if (stepId === "decide" && isPreviewCurrent && preview && !hasDecisionStep) setStepId("confirm");
	}, [stepId, isPreviewCurrent, preview, hasDecisionStep]);

	const decisions = isPreviewCurrent && preview ? buildMergeDecisions(preview, choices) : null;
	const pendingCount = isPreviewCurrent && preview ? pendingDecisionCount(preview, choices) : 0;

	const confirmMerge = useCallback(() => {
		if (!preview || !isPreviewCurrent || !decisions || otherNurseId === null) return;
		track("nurse_merge_confirm_clicked", {
			department_id: departmentId,
			primary_nurse_id: keepNurseId,
			duplicate_nurse_id: otherNurseId,
			department_choice_count: Object.keys(decisions.departmentChoices).length,
			recurring_choice_count: Object.keys(decisions.recurringChoices).length,
			has_shift_overlaps: decisions.overlapKeep !== null,
			entry_point: entryPoint,
		});
		merge.mutate({ primaryNurseId: keepNurseId, duplicateNurseId: otherNurseId, decisions, version: preview.version });
	}, [preview, isPreviewCurrent, decisions, otherNurseId, track, departmentId, keepNurseId, entryPoint, merge]);

	return {
		departmentId,
		timezone: chosenDepartment.timezone,
		stepId,
		setStepId,
		query,
		setQuery,
		isQuerySettling: query.trim() !== debouncedQuery.trim(),
		candidates,
		candidate,
		selectCandidate,
		orderedNurseIds,
		keepNurseId,
		changeKeep,
		previewQuery,
		preview: isSamePair ? preview : undefined,
		isPreviewCurrent,
		hasDecisionStep,
		continueFromCompare,
		choices,
		setChoices,
		pendingCount,
		decisions,
		confirmMerge,
		isMerging: merge.isPending,
		lookups,
	};
};
